/**
 * PARTE 1 — Arquitectura de pasos para las 17 metodologías estándar.
 * Cada metodología tiene: slug único, framework de referencia, y secuencia
 * de pasos que se aplicarán al caso vía IA (Claude) en el endpoint
 * /api/investigations/{case_id}/methodology-detail/{slug}.
 *
 * NO eliminar pasos existentes. Si se agregan nuevas metodologías
 * estándar, agregar entrada nueva con slug único.
 */

export const METHODOLOGY_STEPS = {
  "5 Whys": {
    slug: "5_whys",
    framework: "Kepner-Tregoe / Toyota Production System",
    steps: [
      "Definición del Problema",
      "¿Por qué? — Nivel 1 (primera causa)",
      "¿Por qué? — Nivel 2 (causa de la causa)",
      "¿Por qué? — Nivel 3 (profundizar)",
      "¿Por qué? — Nivel 4 (continuar)",
      "¿Por qué? — Nivel 5 (causa raíz)",
      "Verificación de la Causa Raíz",
      "Conclusiones y Acciones Correctivas",
    ],
  },

  "Ishikawa Diagram": {
    slug: "ishikawa",
    framework: "Kaoru Ishikawa / Diagrama de Espina de Pescado",
    steps: [
      "Definición del Efecto (Problema Central)",
      "Factor: Mano de Obra — causas humanas",
      "Factor: Maquinaria — causas de equipos",
      "Factor: Método — causas de proceso",
      "Factor: Material — causas de insumos",
      "Factor: Medio Ambiente — causas ambientales",
      "Factor: Medición — causas de monitoreo",
      "Causas Primarias Identificadas por Factor",
      "Causas Secundarias (sub-ramas)",
      "Causa Raíz Principal — Conclusiones",
    ],
  },

  "5 Whys Enhanced": {
    slug: "5_whys_enhanced",
    framework: "5 Whys Extendido con Evidencia",
    steps: [
      "Definición del Problema y Alcance",
      "Conformación del Equipo de Análisis",
      "¿Por qué? — Nivel 1 con evidencia documental",
      "¿Por qué? — Nivel 2 con evidencia documental",
      "¿Por qué? — Nivel 3 con evidencia documental",
      "¿Por qué? — Nivel 4 con evidencia documental",
      "¿Por qué? — Nivel 5 con evidencia documental",
      "Verificación de Causalidad",
      "Acciones Correctivas y Responsables",
      "Conclusiones y Cierre",
    ],
  },

  "Ishikawa": {
    slug: "ishikawa_medium",
    framework: "Ishikawa Aplicado — Nivel Medio",
    steps: [
      "Definición del Efecto Central",
      "Análisis — Man (Mano de Obra)",
      "Análisis — Machine (Maquinaria)",
      "Análisis — Method (Método)",
      "Análisis — Material",
      "Análisis — Environment (Medio Ambiente)",
      "Análisis — Measurement (Medición)",
      "Causa Raíz Primaria",
      "Conclusiones",
    ],
  },

  "PDCA": {
    slug: "pdca",
    framework: "Ciclo Deming — Plan Do Check Act",
    steps: [
      "PLAN — Definición del Problema",
      "PLAN — Análisis de Causas",
      "PLAN — Definición de Objetivos y Métricas",
      "PLAN — Soluciones Propuestas",
      "DO — Acciones Implementadas",
      "DO — Recursos utilizados",
      "CHECK — Resultados vs Objetivos",
      "CHECK — Desviaciones y Hallazgos",
      "ACT — Estandarización del Fix",
      "ACT — Próximo Ciclo PDCA — Conclusiones",
    ],
  },

  "8D Report": {
    slug: "8d_report",
    framework: "Ford Motor Company — 8 Disciplines",
    steps: [
      "D1 — Formación del Equipo Multidisciplinario",
      "D2 — Descripción del Problema (5W2H)",
      "D3 — Acciones de Contención Inmediata (ICA)",
      "D4 — Análisis de Causa Raíz (RCA)",
      "D5 — Acciones Correctivas Permanentes (PCA)",
      "D6 — Implementación y Validación de PCA",
      "D7 — Prevención de Recurrencia Sistémica",
      "D8 — Cierre Formal y Reconocimiento del Equipo",
      "Conclusiones",
    ],
  },

  "Comprehensive Cause List": {
    slug: "ccl",
    framework: "CCPS / AIChE — Lista Comprehensiva de Causas",
    steps: [
      "Definición del Incidente y Alcance",
      "Categoría 1 — Factores Técnicos / Hardware",
      "Categoría 2 — Factores Humanos",
      "Categoría 3 — Sistema de Gestión HSE",
      "Categoría 4 — Factores Organizacionales",
      "Categoría 5 — Factores de Diseño",
      "Categoría 6 — Factores Externos",
      "Priorización de Causas por Frecuencia e Impacto",
      "Top 3 Causas Raíz Identificadas",
      "Plan de Acción — Conclusiones",
    ],
  },

  "Apollo RCA": {
    slug: "apollo_rca",
    framework: "Apollo Associated Services — Reality Charting",
    steps: [
      "Definición del Efecto Primario",
      "Construcción del Diagrama Causa-Efecto (Reality Chart)",
      "Identificación de Causas Físicas",
      "Identificación de Causas Humanas",
      "Identificación de Causas Latentes del Sistema",
      "Verificación de Causalidad con Evidencia Documental",
      "Determinación de Causas Raíz Definitivas",
      "Generación de Soluciones por Causa Raíz",
      "Análisis Costo-Beneficio de Soluciones",
      "Conclusiones y Plan de Implementación",
    ],
  },

  "Bow-Tie Analysis": {
    slug: "bow_tie",
    framework: "Shell / IChemE — Bow-Tie Risk Assessment",
    steps: [
      "Identificación del Peligro (Hazard)",
      "Definición del Evento Top (Top Event)",
      "Identificación de Amenazas — Lado Izquierdo",
      "Barreras de Prevención (Pre-Event Controls)",
      "Evaluación de Efectividad de Barreras Preventivas",
      "Identificación de Consecuencias — Lado Derecho",
      "Barreras de Mitigación (Post-Event Controls)",
      "Evaluación de Efectividad de Barreras de Mitigación",
      "Factores de Degradación de Barreras",
      "Conclusiones y Recomendaciones",
    ],
  },

  "Cause Mapping": {
    slug: "cause_mapping",
    framework: "ThinkReliability — Cause Map Method",
    steps: [
      "Definición del Problema e Impacto en Objetivos",
      "Mapa Causal — Nivel 1 (causas directas)",
      "Mapa Causal — Nivel 2 (causas intermedias)",
      "Mapa Causal — Nivel 3 (causas raíz)",
      "Identificación de Soluciones por Nodo Causal",
      "Evaluación de Soluciones (Impacto vs Esfuerzo)",
      "Selección de Soluciones Óptimas",
      "Plan de Acción Priorizado",
      "Conclusiones",
    ],
  },

  "FMEA/FMECA": {
    slug: "fmea",
    framework: "MIL-STD-1629A / SAE J1739 — FMEA",
    steps: [
      "Definición del Alcance y Componentes del Sistema",
      "Identificación de Modos de Falla por Componente",
      "Análisis de Efectos de cada Modo de Falla",
      "Asignación de Severidad (S) — Escala 1 a 10",
      "Asignación de Ocurrencia (O) — Escala 1 a 10",
      "Asignación de Detección (D) — Escala 1 a 10",
      "Cálculo de RPN (Risk Priority Number = S × O × D)",
      "Priorización de Modos de Falla por RPN",
      "Definición de Acciones Correctivas (RPN > 100)",
      "Nuevo RPN Estimado Post-Acción",
      "Conclusiones",
    ],
  },

  "8D Advanced": {
    slug: "8d_advanced",
    framework: "8D Avanzado — Industria O&G / HSEQ",
    steps: [
      "D0 — Evaluación de Emergencia y Criterios de Activación",
      "D1 — Formación del Equipo Multifuncional",
      "D2 — Descripción Detallada del Problema (5W2H extendido)",
      "D3 — Acciones de Contención y Verificación de Efectividad",
      "D4 — Análisis de Causa Raíz (Ishikawa + 5 Whys + FTA)",
      "D5 — Acciones Correctivas Permanentes Propuestas",
      "D6 — Implementación, Validación y Monitoreo de KPIs",
      "D7 — Prevención Sistémica y Actualización de Procedimientos",
      "D8 — Cierre Formal, Lecciones Aprendidas y Reconocimiento",
      "Conclusiones",
    ],
  },

  "Tripod Beta/Delta": {
    slug: "tripod",
    framework: "Shell International / Leiden University, 1992",
    steps: [
      "Descripción del Incidente",
      "Identificación de Causas Inmediatas",
      "BRF 1 — Hardware (fallas de equipo físico)",
      "BRF 2 — Design (problemas de diseño del sistema)",
      "BRF 3 — Maintenance Management (gestión de mantenimiento)",
      "BRF 4 — Procedures (procedimientos inadecuados o ausentes)",
      "BRF 5 — Training (capacitación insuficiente)",
      "BRF 6 — Incompatible Goals (conflictos de objetivos)",
      "BRF 7 — Communication (fallas de comunicación)",
      "BRF 8 — Organization (factores organizacionales)",
      "BRF 9 — Error Enforcing Conditions (condiciones propensas a error)",
      "BRF 10 — Defenses (barreras fallidas o ausentes)",
      "BRF 11 — House Keeping (orden y limpieza)",
      "Identificación de Fallas Latentes del Sistema de Gestión",
      "Conclusiones y Recomendaciones",
    ],
  },

  "ICAM": {
    slug: "icam",
    framework: "BHP Billiton, 2000 — Incident Cause Analysis Method",
    steps: [
      "Recopilación de Información del Incidente",
      "Identificación de Defensas Ausentes o Fallidas",
      "Análisis de Acciones Individuales y del Equipo",
      "Evaluación de Condiciones de Tarea y Ambiente",
      "Identificación de Factores Organizacionales",
      "Análisis de Factores Contribuyentes Sistémicos",
      "Identificación de Causa Raíz Principal",
      "Desarrollo de Recomendaciones por Nivel",
      "Plan de Implementación Priorizado",
      "Conclusiones",
    ],
  },

  "TapRooT®": {
    slug: "taproot",
    framework: "System Improvements Inc. — TapRooT® System",
    steps: [
      "Definición del Alcance de la Investigación",
      "Construcción de la Línea de Tiempo del Incidente",
      "Identificación de Factores Causales (CF)",
      "Árbol TapRooT® — Rama de Supervisión",
      "Árbol TapRooT® — Rama de Comunicación",
      "Árbol TapRooT® — Rama de RRHH",
      "Árbol TapRooT® — Rama de Procedimientos",
      "Análisis de Salvaguardas (SafeguardAnalysis)",
      "Identificación de Causas Raíz",
      "Acciones Correctivas SMARTER",
      "Conclusiones",
    ],
  },

  "DNV SCAT": {
    slug: "dnv_scat",
    framework: "Det Norske Veritas — Systematic Cause Analysis Technique",
    steps: [
      "Identificación de Pérdidas (Lesión / Daño / Proceso)",
      "Análisis del Contacto / Tipo de Incidente",
      "Causas Inmediatas — Actos Subestándar",
      "Causas Inmediatas — Condiciones Subestándar",
      "Causas Básicas — Factores Personales",
      "Causas Básicas — Factores del Trabajo",
      "Análisis de Falta de Control del Sistema de Gestión",
      "Evaluación de Controles Existentes vs Requeridos",
      "Acciones Correctivas Priorizadas",
      "Conclusiones",
    ],
  },

  "Fault Tree Analysis": {
    slug: "fta",
    framework: "Bell Telephone Laboratories / MIL-HDBK-338B",
    steps: [
      "Definición del Evento Tope (Top Event)",
      "Construcción del Árbol — Nivel 1 (puertas AND/OR)",
      "Construcción del Árbol — Nivel 2",
      "Construcción del Árbol — Nivel 3",
      "Identificación de Eventos Básicos (hojas del árbol)",
      "Análisis Cualitativo — Conjuntos Mínimos de Corte (MCS)",
      "Evaluación de Criticidad de cada MCS",
      "Identificación de Causas Raíz por Rama",
      "Acciones de Mitigación por Rama del Árbol",
      "Conclusiones",
    ],
  },

  "MORT": {
    slug: "mort",
    framework: "William G. Johnson / ERDA — Management Oversight Risk Tree",
    steps: [
      "Definición del Incidente Bajo Análisis",
      "Evaluación Rama S — Supervisión y Gestión",
      "Evaluación Rama T — Factores Causales Específicos",
      "Análisis de Barreras y Controles de Energía",
      "Análisis de Políticas y Directrices Organizacionales",
      "Análisis de Implementación del Sistema de Gestión",
      "Análisis de Evaluación de Riesgos Previa al Incidente",
      "Identificación de Omisiones Gerenciales",
      "Causas Raíz del Sistema de Gestión",
      "Conclusiones y Recomendaciones Sistémicas",
    ],
  },

  "STAMP/STPA": {
    slug: "stamp_stpa",
    framework: "MIT — Systems Theoretic Accident Model and Processes",
    steps: [
      "Definición del Propósito y Límites del Sistema",
      "Identificación de Pérdidas del Sistema (Losses)",
      "Identificación de Peligros del Sistema (System Hazards)",
      "Definición de Restricciones de Seguridad del Sistema",
      "Modelado de la Estructura de Control del Sistema",
      "Identificación de Acciones de Control Inseguras (UCAs)",
      "Análisis de Causas UCAs — Controlador",
      "Análisis de Causas UCAs — Canal de Control / Actuadores",
      "Análisis de Causas UCAs — Proceso Controlado",
      "Desarrollo de Requisitos de Mitigación",
      "Conclusiones",
    ],
  },
};

/** Devuelve el slug de una metodología por su nombre (o null si no es estándar) */
export function slugForMethodology(name) {
  return METHODOLOGY_STEPS[name]?.slug || null;
}

/** Devuelve framework + pasos para una metodología por nombre */
export function methodologyArchitecture(name) {
  return METHODOLOGY_STEPS[name] || null;
}
